import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import Button from "components/Button";
import PreventAutoComplete from "components/PreventAutoComplete";
import Loader from "components/Loader";
import { colors } from "globalStyles";
import {
  StyledForm,
  FieldHead,
  FormGroupWithoutBorderBottom,
  UpgradeText,
  ChannelFormSection,
  FormGroup,
  ValidIndicator,
  ValidIndicatorContainer,
} from "./styledComponents";

import {
  ChannelSelectField,
  ChannelInputField,
  ChannelUrlInputField,
  ChannelDateInputField,
  ChannelSwitchField,
  ChannelAdministratorsSelect,
  ChannelGroupRadioInput,
} from "./components";
import TimezonePicker from "react-timezone-select";
import Tooltip from "components/Tooltip";
import Icon from "components/Icon";
import Divider from "components/Divider";
import { t } from "copy/t";
import { validateChannelSlugRequest, setChannelForm } from "../actions";
import { InlineNote } from "./InlineNote";
import { Controller, useWatch } from "react-hook-form";

// Same rule as rails' Channel#no_numbers_in_channel_slug: a link made only of
// digits is rejected with a 422, so say so before probing availability.
export const isAllDigitSlug = (slug) => /^\d+$/.test(slug || "");

const slugFromName = (value) =>
  value
    .toLowerCase()
    .trimStart()
    .replace(/ /g, "-")
    .replace(/[^a-z\-_0-9]/g, "")
    .replace(/[-]+$/, "");

function BasicInfoForm(props) {
  const {
    onSubmit,
    submitting,
    pristine,
    fetchClients,
    clients,
    channelName,
    nextTab,
    edit,
    disabled,
    isPhotographerOwner,
    isPhotographer,
    enterpriseAdmins,
    channelAdministrators,
    permissionRole,
    validateSlug,
    editingChannelId,
    slugIsValid,
    slugIsValidating,
    slugCheckFailed,
    password,
    setChannelForm,
    form,
    user,
  } = props;

  // The availability probe must name the channel being edited so the server can
  // exclude it — otherwise the channel's own live slug and its append-only
  // `slug_history` count as conflicts and the owner can never go back to one of
  // their own former gallery URLs. `editingChannelId` is undefined on the
  // create flow, where there is no channel to exclude yet.
  const probeSlug = useCallback(
    (slug) => {
      if (isAllDigitSlug(slug)) return;
      return validateSlug(slug, editingChannelId);
    },
    [validateSlug, editingChannelId]
  );

  // User name for default values
  const userFirstName = user?.first_name || "";
  const userLastName = user?.last_name || "";
  const userCompany = user?.company || "";
  const userFullName = [userFirstName, userLastName].filter(Boolean).join(" ") || "Your Name";

  // Default account name: prefer company if set, otherwise use full name
  const defaultAccountName = userCompany || (userFullName !== "Your Name" ? userFullName : "");

  // Gallery name placeholder with user's name
  const galleryNamePlaceholder = userFullName !== "Your Name"
    ? `${userFullName}'s Movebooth Gallery`
    : "Your Movebooth Gallery";

  // URL slug placeholder (lowercase, hyphenated)
  const urlSlugPlaceholder = userFullName !== "Your Name"
    ? `${userFirstName.toLowerCase()}-${userLastName.toLowerCase()}s-movebooth-gallery`
    : "your-movebooth-gallery";

  // Current environment root URL for gallery URL prefix
  const galleryUrlPrefix = `${window.location.origin}/ch/`;
  const [channelUrlTimeout, setChannelUrlTimeout] = useState("");
  const { control, getValues, setValue, watch, handleSubmit } = form;

  // Set default client value to user's company (if set) or full name when creating a new channel
  useEffect(() => {
    if (!edit && defaultAccountName && !getValues("client")) {
      setValue("client", defaultAccountName);
    }
  }, [edit, defaultAccountName, setValue, getValues]);

  const clearPassword = () => setValue("password", "");
  const turnOffPassword = () => setValue("password_required", false);
  const galleryUrl = watch("slug");

  const values = useWatch({ control });
  const passwordRequired = values["password_required"];
  const slugAllDigits = isAllDigitSlug(galleryUrl);
  const invalid =
    !watch("client") ||
    !watch("name") ||
    !watch("slug") ||
    slugAllDigits ||
    (passwordRequired && !watch("password"));

  // The first unmet requirement, in plain words, so a disabled Save is never
  // unexplained. `retry` offers a second try when only our check failed.
  let saveReason = null;
  let saveReasonRetry = false;
  if (!watch("client")) saveReason = "Enter an account name.";
  else if (!watch("name")) saveReason = "Enter a gallery name.";
  else if (!galleryUrl) saveReason = "Enter a gallery link.";
  else if (slugAllDigits)
    saveReason = "The gallery link needs at least one letter.";
  else if (passwordRequired && !watch("password"))
    saveReason = "Enter a gallery password.";
  else if (slugIsValidating) saveReason = "Checking that link...";
  else if (slugCheckFailed) {
    saveReason = "Couldn't check availability.";
    saveReasonRetry = true;
  } else if (!slugIsValid)
    saveReason = "That link is already taken. Try another.";
  else if (edit && pristine) saveReason = "Change something to save.";
  const reasonId = "basic-info-save-reason";
  const slugReason =
    !galleryUrl ? null : slugAllDigits || slugCheckFailed || !slugIsValid
      ? saveReason
      : null;

  return (
    <StyledForm autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="basic-info-form">
      <FormGroup>
        <ChannelFormSection>
          <Controller
            control={control}
            name="client"
            render={({ field: { ref, ...fieldRest } }) => (
              <ChannelSelectField
                label="Account Name"
                placeholder={defaultAccountName || userFullName}
                type="text"
                fetchOptions={fetchClients}
                newOptionText="+ Add New Client"
                tooltip="Enter a client name that will help you find this channel later. It might be the name of the person or company whose logo is going to appear in it, or it might be the name of your own company (if different)."
                field={fieldRest}
                {...fieldRest}
              />
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field: { ref, ...fieldRest } }) => (
              <ChannelInputField
                type="text"
                label="Channel/Gallery Name"
                placeholder={galleryNamePlaceholder}
                tooltip="Name this channel something that will help you remember what it is for. For example, if you plan to use this channel only at a specific event, you might use that event name somewhere in the channel name."
                field={fieldRest}
                {...fieldRest}
                onChange={(e) => {
                  fieldRest.onChange(e.target.value);
                  if (!galleryUrl || slugFromName(e.target.value) !== galleryUrl) {
                    setValue("slug", slugFromName(e.target.value));
                  }
                }}
                onBlur={() => {
                  fieldRest.onBlur();
                  // Fill the link from the name as soon as the name is done,
                  // not only once the link field is focused.
                  const name = getValues("name");
                  if (!edit && name && !getValues("slug")) {
                    setValue("slug", slugFromName(name));
                  }
                }}
              />
            )}
          />
          <ValidIndicatorContainer>
            <Controller
              control={control}
              name="slug"
              key="slug"
              render={({ field: { ref, ...fieldRest } }) => (
                <ChannelUrlInputField
                  type="text"
                  autoComplete="off"
                  label="Gallery URL"
                  placeholder={urlSlugPlaceholder}
                  urlPrefix={galleryUrlPrefix}
                  channelName={channelName}
                  validateSlug={probeSlug}
                  channelUrlTimeout={channelUrlTimeout}
                  setChannelUrlTimeout={(timeoutObj) =>
                    setChannelUrlTimeout(timeoutObj)
                  }
                  aria-describedby={slugReason ? reasonId : undefined}
                  tooltip={`This is the web address where the online gallery of images for this channel will live. We'll create a suggested URL for you based on the channel name you entered, but you can change it to whatever you like (as long as the URL hasn't already been taken)`}
                  field={fieldRest}
                  {...fieldRest}
                />
              )}
            />
            {typeof galleryUrl !== "undefined" ? (
              <ValidIndicator>
                {slugIsValidating ? (
                  <Loader color={colors.MAIN_COLOR} />
                ) : slugIsValid && !slugAllDigits ? (
                  <Tooltip
                    className="tooltip"
                    width="200px"
                    text={`Nice choice! This URL looks good on you.`}
                  >
                    <Icon name="check" size={15} fill={colors.GREEN} />
                  </Tooltip>
                ) : (
                  <Tooltip
                    className="tooltip"
                    width="200px"
                    text={
                      slugAllDigits
                        ? `The gallery link needs at least one letter.`
                        : slugCheckFailed
                        ? `Couldn't check availability. Try again.`
                        : `This URL is already taken. Please choose another.`
                    }
                  >
                    <Icon name="x" size={13} fill={colors.RED} />
                  </Tooltip>
                )}
              </ValidIndicator>
            ) : (
              ""
            )}
          </ValidIndicatorContainer>
          {
            <FormGroupWithoutBorderBottom>
              <FieldHead>
                <div>
                  <strong>password protect gallery</strong>
                  <h3 className=" md:max-w-[25ch] lg:max-w-full w-full">Require a password to access the online gallery</h3>
                </div>
                <div>
                  <Controller
                    control={control}
                    name="password_required"
                    render={({ field: { ref, ...fieldRest } }) => (
                      <ChannelSwitchField
                        type="password"
                        aria-label="Password protect gallery"
                        clearPassword={clearPassword}
                        turnOffPassword={turnOffPassword}
                        password={password}
                        checked={fieldRest.value || values.password}
                        {...fieldRest}
                        onChange={(e) => {
                          setValue(
                            "password_required",
                            !(fieldRest.value || false)
                          );
                          // fieldRest.onChange(e);
                          // if (fieldRest.value === true && e === false) {
                          //   clearPassword();
                          //   turnOffPassword();
                          // }
                        }}
                        field={fieldRest}
                      />
                    )}
                  />
                </div>
              </FieldHead>
              <div
                className={passwordRequired || values.password ? "block" : "hidden"}
              >
                <Controller
                  control={control}
                  name="password"
                  render={({ field: { ref, ...fieldRest } }) => (
                    <ChannelInputField
                      type="password"
                      autoComplete="off"
                      showPasswordButton={true}
                      label="Gallery Password"
                      placeholder={t("placeholders.password")}
                      field={fieldRest}
                      {...fieldRest}
                    />
                  )}
                />
              </div>
            </FormGroupWithoutBorderBottom>
          }
          {!isPhotographer && !edit && (
            <FormGroupWithoutBorderBottom className="customization-type">
              <FieldHead className="tooltip">
                <div>
                  <strong>customization type</strong>
                </div>
                <Tooltip
                  className="tooltip"
                  width="200px"
                  text={`If the client or event for this channel doesn't already have graphics you can use (like a logo and photo filters), you could use one of our Movebooth themes instead. This option is really popular for weddings and other personal events`}
                >
                  <Icon size="12" name="help" />
                </Tooltip>
              </FieldHead>
              <Controller
                control={control}
                name="useMoveboothTheme"
                render={({ field: { ref, ...fieldRest } }) => (
                  <ChannelGroupRadioInput field={fieldRest} {...fieldRest} />
                )}
              />
            </FormGroupWithoutBorderBottom>
          )}
          {
            <Controller
              control={control}
              name={isPhotographerOwner ? "event_date" : "active_until"}
              render={({ field: { ref, ...fieldRest } }) => (
                <ChannelDateInputField
                  type="date"
                  label={isPhotographerOwner ? "Event Date" : "Active Until"}
                  placeholder={
                    isPhotographerOwner
                      ? t("placeholders.selectEventDate")
                      : t("placeholders.noEndDate")
                  }
                  required={isPhotographerOwner ? true : false}
                  disabled={isPhotographerOwner ? disabled : false}
                  style={{ "z-index": 1 }}
                  field={fieldRest}
                  {...fieldRest}
                />
              )}
            />
          }
          {isPhotographer ? (
            <div>
              <label className="channel-forms__basic-info-event-time-zone-label">
                {" "}
                Event Time Zone{" "}
              </label>
              <div>
                <Controller
                  control={control}
                  name="time_zone"
                  render={({ field: { ref, onChange, value, ...fieldRest } }) => (
                    <TimezonePicker
                      type=""
                      required={true}
                      inputProps={{
                        placeholder: "Select Timezone...",
                      }}
                      // v3 always emits the full ITimezoneOption object; the form state and
                      // the API contract (channels.js `time_zone`, openapi: string) are the
                      // plain IANA id (e.g. "America/New_York") — normalize at this boundary
                      // so nothing downstream ever sees the object. v3's `value` prop accepts
                      // the stored string directly (it parses via parseTimezone).
                      value={value || ""}
                      onChange={(tz) =>
                        onChange(typeof tz === "string" ? tz : (tz && tz.value) || "")
                      }
                      {...fieldRest}
                    />
                  )}
                />
              </div>
            </div>
          ) : (
            ""
          )}
          {permissionRole == "super_admin" ? (
            <div>
              <Controller
                control={control}
                name="channel_administrators"
                render={({ field: { ref, ...fieldRest } }) => (
                  <ChannelAdministratorsSelect
                    className="custom-select"
                    enterpriseAdmins={enterpriseAdmins}
                    multi={true}
                    selectedChannelAdministrators={
                      getValues("channel_administrators") ||
                      channelAdministrators
                    }
                    placeholder={t("placeholders.selectChannelAdministrators")}
                    field={fieldRest}
                    {...fieldRest}
                  />
                )}
              />
            </div>
          ) : (
            ""
          )}
          <PreventAutoComplete />
        </ChannelFormSection>
      </FormGroup>
      <Divider marginBottom={48}></Divider>
      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit ? (
          <Button
            type="submit"
            aria-describedby={saveReason ? reasonId : undefined}
            disabled={
              submitting ||
              pristine ||
              invalid ||
              !slugIsValid ||
              slugIsValidating
            }
          >
            Save
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => {
              setChannelForm(values);
              nextTab();
            }}
            aria-describedby={saveReason ? reasonId : undefined}
            disabled={
              submitting ||
              pristine ||
              invalid ||
              !slugIsValid ||
              slugIsValidating
            }
          >
            Save and Next
          </Button>
        )}
        {saveReason && (
          <InlineNote
            id={reasonId}
            onRetry={
              saveReasonRetry ? () => probeSlug(galleryUrl) : undefined
            }
          >
            {saveReason}
          </InlineNote>
        )}
      </div>
    </StyledForm>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    user: state.app.user,
    slugCheckFailed: Boolean(state.channels.slugCheckFailed),
    channelAdministrators:
      state.channels.channelDetails &&
      state.channels.channelDetails.users.map((u) => ({
        value: u.id,
        label: u.first_name,
      })),
    // Only the EDIT flow has a channel for the server to exclude from the
    // slug-conflict search. `channelDetail` (singular) is the loaded channel —
    // it survives across navigations, so this must key off `edit` rather than
    // on the mere presence of a channel in the store, or the create flow would
    // exclude an unrelated channel.
    editingChannelId: ownProps.edit ? state.channels.channelDetail?.id : undefined,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    validateSlug: (slug, channelId) =>
      dispatch(validateChannelSlugRequest(slug, channelId)),
    setChannelForm: (value) => dispatch(setChannelForm(value)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(BasicInfoForm);
